/**
 * Service Resources Management Page
 * إدارة عناصر العرض للخدمات (مستشارين، برامج، فعاليات)
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  UserCheck, GraduationCap, Calendar, Building, Briefcase, Globe, Users,
  Package, Plus, Edit2, Trash2, ToggleLeft, ToggleRight, X, Check, 
  AlertTriangle, Info, Search, Filter, RefreshCw, ChevronDown, Eye,
  MapPin, Clock, Phone, Link2, DollarSign
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api/catalog';

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN SYSTEM
// ═══════════════════════════════════════════════════════════════════════════════

const COLORS = {
  primary: '#1D8F4E',
  primaryLight: '#E8F5EE',
  secondary: '#1B3A6B',
  secondaryLight: '#EEF2F7',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  success: '#059669',
  warning: '#D97706',
  error: '#DC2626',
  info: '#2563EB',
};

const RESOURCE_TYPE_ICONS = {
  consultant: UserCheck,
  training_program: GraduationCap,
  event: Calendar,
  branch_service: Building,
  workspace_package: Briefcase,
  digital_service: Globe,
  workshop: Users,
  other: Package,
};

const RESOURCE_TYPE_COLORS = {
  consultant: '#8B5CF6',
  training_program: '#F59E0B',
  event: '#EF4444',
  branch_service: '#10B981',
  workspace_package: '#3B82F6',
  digital_service: '#06B6D4',
  workshop: '#EC4899',
  other: '#6B7280',
};

// ═══════════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

function getAuthHeaders() {
  const token = localStorage.getItem('hala_admin_token');
  return { Authorization: `Bearer ${token}` };
}

// ═══════════════════════════════════════════════════════════════════════════════
// UI COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

function Alert({ type = 'info', children, style = {} }) {
  const configs = {
    info: { bg: '#EFF6FF', border: '#3B82F6', color: '#1D4ED8', Icon: Info },
    warning: { bg: '#FFFBEB', border: '#F59E0B', color: '#B45309', Icon: AlertTriangle },
    error: { bg: '#FEF2F2', border: '#EF4444', color: '#B91C1C', Icon: AlertTriangle },
    success: { bg: '#F0FDF4', border: '#22C55E', color: '#15803D', Icon: Check },
  };
  const config = configs[type];
  return (
    <div style={{
      padding: '16px 20px', borderRadius: '12px', backgroundColor: config.bg,
      borderRight: `4px solid ${config.border}`, display: 'flex', alignItems: 'flex-start',
      gap: '12px', ...style
    }}>
      <config.Icon size={20} color={config.color} style={{ flexShrink: 0, marginTop: '2px' }} />
      <div style={{ fontSize: '14px', color: config.color, lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

function Button({ children, variant = 'primary', size = 'md', icon: Icon, loading, disabled, onClick, style = {} }) {
  const variants = {
    primary: { bg: COLORS.primary, color: '#fff', hover: '#157a3f' },
    secondary: { bg: COLORS.secondary, color: '#fff', hover: '#152d55' },
    outline: { bg: 'transparent', color: COLORS.text, border: COLORS.border, hover: COLORS.bg },
    ghost: { bg: 'transparent', color: COLORS.textMuted, hover: COLORS.bg },
    danger: { bg: COLORS.error, color: '#fff', hover: '#b91c1c' },
    success: { bg: COLORS.success, color: '#fff', hover: '#047857' },
  };
  const sizes = {
    sm: { padding: '8px 14px', fontSize: '13px', gap: '6px' },
    md: { padding: '10px 18px', fontSize: '14px', gap: '8px' },
    lg: { padding: '12px 24px', fontSize: '15px', gap: '10px' },
  };
  const v = variants[variant];
  const s = sizes[size];
  
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        gap: s.gap, padding: s.padding, fontSize: s.fontSize, fontWeight: 600,
        fontFamily: "'Tajawal', sans-serif",
        borderRadius: '10px', border: v.border ? `1.5px solid ${v.border}` : 'none',
        backgroundColor: v.bg, color: v.color, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1, transition: 'all 0.2s', ...style
      }}
    >
      {loading ? <RefreshCw size={16} className="animate-spin" /> : Icon && <Icon size={16} />}
      {children}
    </button>
  );
}

function Input({ value, onChange, placeholder, type = 'text', style = {} }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: '100%', padding: '12px 14px', borderRadius: '10px',
        border: `1.5px solid ${COLORS.border}`, backgroundColor: COLORS.white,
        fontSize: '14px', fontFamily: "'Tajawal', sans-serif", color: COLORS.text,
        outline: 'none', ...style
      }}
    />
  );
}

function Select({ value, onChange, options, placeholder, style = {} }) {
  return (
    <select
      value={value}
      onChange={onChange}
      style={{
        width: '100%', padding: '12px 14px', borderRadius: '10px',
        border: `1.5px solid ${COLORS.border}`, backgroundColor: COLORS.white,
        fontSize: '14px', fontFamily: "'Tajawal', sans-serif", color: COLORS.text,
        cursor: 'pointer', outline: 'none', ...style
      }}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

function FormField({ label, required, hint, children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{
        display: 'block', marginBottom: '8px', fontSize: '14px',
        fontWeight: 600, color: COLORS.text,
      }}>
        {label}
        {required && <span style={{ color: COLORS.error, marginRight: '4px' }}>*</span>}
        {hint && <span style={{ fontWeight: 400, color: COLORS.textMuted, marginRight: '8px' }}>({hint})</span>}
      </label>
      {children}
    </div>
  );
}

function Badge({ color, icon: Icon, children }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '6px 12px', borderRadius: '8px',
      backgroundColor: `${color}15`, color: color,
      fontSize: '13px', fontWeight: 500,
    }}>
      {Icon && <Icon size={14} />}
      {children}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// RESOURCE CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

function ResourceCard({ resource, onEdit, onToggle, onDelete }) {
  const Icon = RESOURCE_TYPE_ICONS[resource.resource_type] || Package;
  const color = RESOURCE_TYPE_COLORS[resource.resource_type] || COLORS.textMuted;
  
  return (
    <div style={{
      padding: '20px', borderRadius: '12px', backgroundColor: COLORS.white,
      border: `1px solid ${COLORS.border}`, opacity: resource.is_active ? 1 : 0.6,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
        {/* Icon */}
        <div style={{
          width: '48px', height: '48px', borderRadius: '12px',
          backgroundColor: `${color}15`, display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon size={24} color={color} />
        </div>
        
        {/* Content */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
              {resource.name_ar}
            </h4>
            <Badge color={color}>
              {resource.resource_type_name_ar}
            </Badge>
          </div>
          
          {resource.short_description && (
            <p style={{ margin: '8px 0 0', fontSize: '14px', color: COLORS.textMuted, lineHeight: 1.5 }}>
              {resource.short_description}
            </p>
          )}
          
          {/* Meta Info */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '12px' }}>
            {resource.city && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: COLORS.textMuted }}>
                <MapPin size={14} /> {resource.city}
              </span>
            )}
            {resource.duration && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: COLORS.textMuted }}>
                <Clock size={14} /> {resource.duration}
              </span>
            )}
            {resource.provider_name && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: COLORS.textMuted }}>
                <UserCheck size={14} /> {resource.provider_name}
              </span>
            )}
            {resource.is_free ? (
              <Badge color={COLORS.success}>مجاني</Badge>
            ) : resource.price && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: COLORS.text, fontWeight: 500 }}>
                <DollarSign size={14} /> {resource.price} ر.س
              </span>
            )}
          </div>
          
          {/* Service link */}
          {resource.service_name_ar && (
            <div style={{ marginTop: '12px', padding: '8px 12px', backgroundColor: COLORS.bg, borderRadius: '8px' }}>
              <span style={{ fontSize: '12px', color: COLORS.textMuted }}>الخدمة: </span>
              <span style={{ fontSize: '13px', fontWeight: 500, color: COLORS.text }}>{resource.service_name_ar}</span>
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => onToggle(resource)}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 12px',
              borderRadius: '8px', border: 'none', backgroundColor: 'transparent',
              color: resource.is_active ? COLORS.success : COLORS.textMuted,
              cursor: 'pointer', fontSize: '13px',
            }}
            title={resource.is_active ? 'تعطيل' : 'تفعيل'}
          >
            {resource.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
          </button>
          <button
            onClick={() => onEdit(resource)}
            style={{
              display: 'flex', alignItems: 'center', padding: '8px',
              borderRadius: '8px', border: 'none', backgroundColor: COLORS.secondaryLight,
              color: COLORS.secondary, cursor: 'pointer',
            }}
            title="تعديل"
          >
            <Edit2 size={16} />
          </button>
          <button
            onClick={() => onDelete(resource)}
            style={{
              display: 'flex', alignItems: 'center', padding: '8px',
              borderRadius: '8px', border: 'none', backgroundColor: '#FEE2E2',
              color: COLORS.error, cursor: 'pointer',
            }}
            title="حذف"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ADD/EDIT RESOURCE MODAL
// ═══════════════════════════════════════════════════════════════════════════════

function ResourceModal({ isOpen, onClose, onSave, services, resourceTypes, editingResource }) {
  const [formData, setFormData] = useState({
    service_id: '',
    resource_type: '',
    name_ar: '',
    name_en: '',
    short_description: '',
    details: '',
    city: '',
    branch_name: '',
    provider_name: '',
    schedule_info: '',
    booking_link: '',
    contact_info: '',
    duration: '',
    price: '',
    is_free: true,
    display_order: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  useEffect(() => {
    if (isOpen) {
      if (editingResource) {
        setFormData({
          service_id: editingResource.service_id || '',
          resource_type: editingResource.resource_type || '',
          name_ar: editingResource.name_ar || '',
          name_en: editingResource.name_en || '',
          short_description: editingResource.short_description || '',
          details: editingResource.details || '',
          city: editingResource.city || '',
          branch_name: editingResource.branch_name || '',
          provider_name: editingResource.provider_name || '',
          schedule_info: editingResource.schedule_info || '',
          booking_link: editingResource.booking_link || '',
          contact_info: editingResource.contact_info || '',
          duration: editingResource.duration || '',
          price: editingResource.price || '',
          is_free: editingResource.is_free !== false,
          display_order: editingResource.display_order || 0,
        });
      } else {
        setFormData({
          service_id: '', resource_type: '', name_ar: '', name_en: '',
          short_description: '', details: '', city: '', branch_name: '',
          provider_name: '', schedule_info: '', booking_link: '', contact_info: '',
          duration: '', price: '', is_free: true, display_order: 0,
        });
      }
      setError('');
    }
  }, [isOpen, editingResource]);
  
  const handleSubmit = async () => {
    if (!formData.service_id) {
      setError('يجب اختيار الخدمة');
      return;
    }
    if (!formData.resource_type) {
      setError('يجب اختيار نوع المورد');
      return;
    }
    if (!formData.name_ar.trim()) {
      setError('اسم المورد بالعربية مطلوب');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await onSave(formData);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'حدث خطأ أثناء الحفظ');
    }
    setLoading(false);
  };
  
  if (!isOpen) return null;
  
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999 }}>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)' }} />
      
      {/* Modal */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '700px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto',
        backgroundColor: COLORS.white, borderRadius: '16px',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, backgroundColor: COLORS.white, zIndex: 1,
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: COLORS.text }}>
            {editingResource ? 'تعديل عنصر العرض' : 'إضافة عنصر عرض جديد'}
          </h2>
          <button
            onClick={onClose}
            style={{
              width: '36px', height: '36px', borderRadius: '8px', border: 'none',
              backgroundColor: COLORS.bg, cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={20} color={COLORS.textMuted} />
          </button>
        </div>
        
        {/* Content */}
        <div style={{ padding: '24px' }}>
          {error && <Alert type="error" style={{ marginBottom: '20px' }}>{error}</Alert>}
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormField label="الخدمة" required>
              <Select
                value={formData.service_id}
                onChange={e => setFormData({ ...formData, service_id: e.target.value })}
                placeholder="-- اختر الخدمة --"
                options={services.map(s => ({ value: s.service_id, label: s.name_ar }))}
              />
            </FormField>
            
            <FormField label="نوع المورد" required>
              <Select
                value={formData.resource_type}
                onChange={e => setFormData({ ...formData, resource_type: e.target.value })}
                placeholder="-- اختر النوع --"
                options={resourceTypes.map(t => ({ value: t.type, label: t.name_ar }))}
              />
            </FormField>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormField label="الاسم بالعربية" required>
              <Input
                value={formData.name_ar}
                onChange={e => setFormData({ ...formData, name_ar: e.target.value })}
                placeholder="مثال: برنامج ريادة الأعمال"
              />
            </FormField>
            
            <FormField label="الاسم بالإنجليزية" hint="اختياري">
              <Input
                value={formData.name_en}
                onChange={e => setFormData({ ...formData, name_en: e.target.value })}
                placeholder="Example: Entrepreneurship Program"
              />
            </FormField>
          </div>
          
          <FormField label="وصف مختصر" hint="اختياري">
            <Input
              value={formData.short_description}
              onChange={e => setFormData({ ...formData, short_description: e.target.value })}
              placeholder="وصف مختصر يظهر للمستخدم"
            />
          </FormField>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormField label="المدينة" hint="اختياري">
              <Input
                value={formData.city}
                onChange={e => setFormData({ ...formData, city: e.target.value })}
                placeholder="مثال: الرياض"
              />
            </FormField>
            
            <FormField label="مقدم الخدمة" hint="اختياري">
              <Input
                value={formData.provider_name}
                onChange={e => setFormData({ ...formData, provider_name: e.target.value })}
                placeholder="مثال: م. أحمد العمري"
              />
            </FormField>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormField label="المدة" hint="اختياري">
              <Input
                value={formData.duration}
                onChange={e => setFormData({ ...formData, duration: e.target.value })}
                placeholder="مثال: 3 ساعات"
              />
            </FormField>
            
            <FormField label="معلومات الجدول" hint="اختياري">
              <Input
                value={formData.schedule_info}
                onChange={e => setFormData({ ...formData, schedule_info: e.target.value })}
                placeholder="مثال: كل أحد 10 صباحاً"
              />
            </FormField>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormField label="السعر">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_free}
                    onChange={e => setFormData({ ...formData, is_free: e.target.checked, price: e.target.checked ? '' : formData.price })}
                  />
                  مجاني
                </label>
                {!formData.is_free && (
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={e => setFormData({ ...formData, price: e.target.value })}
                    placeholder="السعر بالريال"
                    style={{ width: '150px' }}
                  />
                )}
              </div>
            </FormField>
            
            <FormField label="رابط الحجز" hint="اختياري">
              <Input
                value={formData.booking_link}
                onChange={e => setFormData({ ...formData, booking_link: e.target.value })}
                placeholder="https://..."
              />
            </FormField>
          </div>
          
          <FormField label="معلومات التواصل" hint="اختياري">
            <Input
              value={formData.contact_info}
              onChange={e => setFormData({ ...formData, contact_info: e.target.value })}
              placeholder="رقم الهاتف أو البريد الإلكتروني"
            />
          </FormField>
        </div>
        
        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: `1px solid ${COLORS.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: '12px',
          backgroundColor: COLORS.bg, position: 'sticky', bottom: 0,
        }}>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button variant="success" icon={Check} onClick={handleSubmit} loading={loading}>
            {editingResource ? 'حفظ التعديلات' : 'إضافة المورد'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function ResourcesPage() {
  const [resources, setResources] = useState([]);
  const [services, setServices] = useState([]);
  const [resourceTypes, setResourceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showModal, setShowModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  
  // Filters
  const [filterService, setFilterService] = useState('');
  const [filterType, setFilterType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [resRes, svcRes, typesRes] = await Promise.all([
        axios.get(`${API}/resources`, { headers: getAuthHeaders() }),
        axios.get(`${API}/services`, { headers: getAuthHeaders() }),
        axios.get(`${API}/resource-types`, { headers: getAuthHeaders() }),
      ]);
      
      setResources(resRes.data || []);
      const svcsData = svcRes.data.items || svcRes.data;
      setServices(Array.isArray(svcsData) ? svcsData : []);
      setResourceTypes(typesRes.data || []);
    } catch (e) {
      console.error('Failed to fetch data:', e);
    }
    setLoading(false);
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Handlers
  const handleSaveResource = async (data) => {
    if (editingResource) {
      await axios.put(`${API}/resources/${editingResource.resource_id}`, data, { headers: getAuthHeaders() });
    } else {
      await axios.post(`${API}/resources`, data, { headers: getAuthHeaders() });
    }
    fetchData();
    setEditingResource(null);
  };
  
  const handleToggleResource = async (resource) => {
    await axios.post(`${API}/resources/${resource.resource_id}/toggle`, {}, { headers: getAuthHeaders() });
    fetchData();
  };
  
  const handleDeleteResource = async (resource) => {
    if (!window.confirm(`هل أنت متأكد من حذف "${resource.name_ar}"؟`)) return;
    await axios.delete(`${API}/resources/${resource.resource_id}`, { headers: getAuthHeaders() });
    fetchData();
  };
  
  // Filtered resources
  const filteredResources = resources.filter(res => {
    if (filterService && res.service_id !== filterService) return false;
    if (filterType && res.resource_type !== filterType) return false;
    if (searchQuery && !res.name_ar.includes(searchQuery) && !res.name_en?.includes(searchQuery)) return false;
    return true;
  });
  
  // Stats
  const activeCount = resources.filter(r => r.is_active).length;
  const typeStats = resourceTypes.map(t => ({
    ...t,
    count: resources.filter(r => r.resource_type === t.type).length
  }));
  
  return (
    <div style={{ padding: '24px', backgroundColor: COLORS.bg, minHeight: '100vh' }}>
      {/* Page Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 8px', fontSize: '28px', fontWeight: 700, color: COLORS.text }}>
          عناصر العرض
        </h1>
        <p style={{ margin: 0, fontSize: '15px', color: COLORS.textMuted }}>
          إدارة عناصر العرض للخدمات (مستشارين، برامج تدريبية، فعاليات، وغيرها)
        </p>
      </div>
      
      {/* Info Box */}
      <Alert type="info" style={{ marginBottom: '24px' }}>
        <strong>ما هي عناصر العرض؟</strong><br />
        عناصر العرض هي المحتوى الفعلي الذي يظهر للمستخدم عند اختيار خدمة معينة.
        مثال: خدمة "الاستشارات" قد تحتوي على عدة مستشارين، وخدمة "التدريب" قد تحتوي على عدة برامج تدريبية.
      </Alert>
      
      {/* Stats Row */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px',
        marginBottom: '24px',
      }}>
        <div style={{
          padding: '16px', borderRadius: '12px', backgroundColor: COLORS.white,
          border: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ fontSize: '24px', fontWeight: 700, color: COLORS.secondary }}>{resources.length}</div>
          <div style={{ fontSize: '13px', color: COLORS.textMuted }}>إجمالي العناصر</div>
        </div>
        <div style={{
          padding: '16px', borderRadius: '12px', backgroundColor: COLORS.white,
          border: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ fontSize: '24px', fontWeight: 700, color: COLORS.success }}>{activeCount}</div>
          <div style={{ fontSize: '13px', color: COLORS.textMuted }}>عناصر نشطة</div>
        </div>
        {typeStats.slice(0, 4).map(t => (
          <div key={t.type} style={{
            padding: '16px', borderRadius: '12px', backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.border}`,
          }}>
            <div style={{ fontSize: '24px', fontWeight: 700, color: RESOURCE_TYPE_COLORS[t.type] }}>{t.count}</div>
            <div style={{ fontSize: '13px', color: COLORS.textMuted }}>{t.name_ar}</div>
          </div>
        ))}
      </div>
      
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '20px', gap: '16px', flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', gap: '12px', flex: 1, flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ position: 'relative', minWidth: '200px' }}>
            <Search size={18} color={COLORS.textMuted} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }} />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="بحث..."
              style={{ paddingRight: '40px' }}
            />
          </div>
          
          {/* Filter by Service */}
          <Select
            value={filterService}
            onChange={e => setFilterService(e.target.value)}
            placeholder="كل الخدمات"
            options={services.map(s => ({ value: s.service_id, label: s.name_ar }))}
            style={{ width: '200px' }}
          />
          
          {/* Filter by Type */}
          <Select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            placeholder="كل الأنواع"
            options={resourceTypes.map(t => ({ value: t.type, label: t.name_ar }))}
            style={{ width: '180px' }}
          />
        </div>
        
        <Button variant="primary" icon={Plus} onClick={() => { setEditingResource(null); setShowModal(true); }}>
          إضافة عنصر عرض
        </Button>
      </div>
      
      {/* Resources List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: COLORS.textMuted }}>
            <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
            <p>جاري التحميل...</p>
          </div>
        ) : filteredResources.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '60px', backgroundColor: COLORS.white,
            borderRadius: '16px', border: `1px solid ${COLORS.border}`,
          }}>
            <Package size={48} color={COLORS.textMuted} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600, color: COLORS.text }}>
              لا توجد عناصر عرض
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: COLORS.textMuted }}>
              {searchQuery || filterService || filterType
                ? 'لا توجد نتائج تطابق البحث'
                : 'قم بإضافة عناصر عرض للخدمات ليراها المستخدمون'}
            </p>
            <Button variant="primary" icon={Plus} onClick={() => { setEditingResource(null); setShowModal(true); }}>
              إضافة أول عنصر
            </Button>
          </div>
        ) : (
          filteredResources.map(res => (
            <ResourceCard
              key={res.resource_id}
              resource={res}
              onEdit={(r) => { setEditingResource(r); setShowModal(true); }}
              onToggle={handleToggleResource}
              onDelete={handleDeleteResource}
            />
          ))
        )}
      </div>
      
      {/* Add/Edit Modal */}
      <ResourceModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingResource(null); }}
        onSave={handleSaveResource}
        services={services}
        resourceTypes={resourceTypes}
        editingResource={editingResource}
      />
    </div>
  );
}
